import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Giriş başarılı");
      nav("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Image side */}
      <div
        className="hidden lg:block relative overflow-hidden"
        style={{
          backgroundImage:
            "url('https://customer-assets-4nw71qhi.emergentagent.net/job_fabric-roll-tracker-1/artifacts/0b3siri2_Facetune_19-07-2025-00-49-50.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-primary/60 via-primary/30 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-primary-foreground">
          <div className="overline text-primary-foreground/80">Kumaş Stok Yönetimi</div>
          <h1 className="font-heading text-5xl font-semibold mt-2 tracking-tight">
            Her top<br />ayrı bir hikaye.
          </h1>
          <p className="mt-4 max-w-sm text-primary-foreground/90 text-sm">
            Depodaki her kumaş topunu barkod, metre ve kesim geçmişiyle bağımsız olarak takip edin.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div>
            <img src="/mossline-logo.png" alt="Mossline Home Textile" className="h-12 w-auto mb-6" />
            <div className="overline text-primary">Stok Programı</div>
            <h2 className="font-heading text-3xl font-semibold mt-1">
              Giriş Yap
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              Devam etmek için e-posta ve şifrenizi girin.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">E-posta</Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
                placeholder="ornek@firma.com"
              />
            </div>
            <div>
              <Label htmlFor="password">Şifre</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  data-testid="login-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 rounded"
                  aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                  data-testid="login-password-toggle"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash size={16} weight="duotone" /> : <Eye size={16} weight="duotone" />}
                </button>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full h-11 font-heading"
          >
            {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
          </Button>

          <div className="text-xs text-muted-foreground border-t border-border pt-4">
            İlk kullanım için yöneticinizden hesap bilgilerinizi alın.
          </div>
        </form>
      </div>
    </div>
  );
}
